import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import { canCheckIn, canManage } from "../lib/roles.js";
import { useToaster } from "../components/Toaster.jsx";
import { VoltageDial, BatteryGlyph, ChargingHalo } from "../components/BatteryViz.jsx";
import {
  subscribeUnit, recordCheckIn, ACTIONS,
  chargeCycleStatus, daysSinceCheck, elapsedCharging,
  setStickyNote, setLotPosition, setSoldFlag,
  relativeTime, authorFromProfile,
} from "../lib/data.js";

// Check-in form. Post 2026-05-07 the lot walk is strictly time-based —
// voltage is optional here, the form's job is to record an action that
// resets/advances the charge cycle, and to capture the per-unit metadata
// (sticky note, lot position) techs need to keep on the car.
//
// Action defaults to "Charge Complete" because that's the cycle-resetting
// happy path. "Charging" flips the unit into the charging-now bucket on
// the lot walk; "Battery Replaced" is a hardware swap; "Checked - OK" is
// the legacy "I walked over and looked at it" no-op.
export function CheckIn() {
  const { id } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [voltage, setVoltage] = useState("");
  const [action, setAction]   = useState("Charge Complete");
  const [notes, setNotes]     = useState("");
  const [stickyNote, setSticky] = useState("");
  const [lotPos, setLot]      = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [soldPending, setSoldPending] = useState(false);
  const { toast } = useToaster();

  useEffect(() => {
    if (!id) return;
    // Defense-in-depth: only render a unit in the caller's tenant + accessible
    // stores, even if rules ever regress.
    const guard = { tenantId: profile?.tenantId, storeAccess: profile?.storeAccess };
    return subscribeUnit(id, (u, err) => {
      if (err) { setLoadError(err); return; }
      setUnit(u);
      // Hydrate the editable fields from the doc the FIRST time it loads.
      // After that, leave them alone so the user's typing isn't clobbered
      // by a Firestore snapshot landing.
      setSticky((cur) => cur || (u.note   || ""));
      setLot   ((cur) => cur || (u.lotPos || ""));
    }, guard);
  }, [id, profile?.tenantId]);

  const writable = canCheckIn(profile);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitError(null);
    const trimmed = voltage.trim();
    let v = null;
    if (trimmed !== "") {
      const parsed = parseFloat(trimmed);
      if (Number.isNaN(parsed)) {
        setSubmitError(new Error("Voltage must be a number, or leave it empty"));
        return;
      }
      v = parsed;
    }
    setSubmitting(true);
    try {
      // Persist sticky note + lot position alongside the check-in.
      // These are independent writes (separate fields, no transactional
      // dependency on the check-in) — fire them in parallel.
      const writes = [
        recordCheckIn({
          unitId: id,
          voltage: v,
          action,
          notes,
          by: authorFromProfile(profile),
        }),
      ];
      if ((stickyNote || "") !== (unit?.note   || "")) writes.push(setStickyNote (id, stickyNote));
      if ((lotPos     || "") !== (unit?.lotPos || "")) writes.push(setLotPosition(id, lotPos));
      await Promise.all(writes);
      toast.ok(`${unit?.stockNumber || "Unit"} — ${action.toLowerCase()}`);
      navigate("/inventory");
    } catch (err) {
      setSubmitError(err);
      toast.error(`Save failed: ${err.message || String(err)}`);
    } finally {
      setSubmitting(false);
    }
  }

  // Manager-only — toggle the sold flag without re-importing the DAR.
  async function toggleSold() {
    if (!canManage(profile) || !unit) return;
    const next = !unit.sold;
    setSoldPending(true);
    try {
      await setSoldFlag(id, next);
      toast.ok(next
        ? `${unit.stockNumber || "Unit"} marked sold — dropped from queues`
        : `${unit.stockNumber || "Unit"} reactivated — back in cycle`);
    } catch (err) {
      toast.error(`Couldn't update sold flag: ${err.message || String(err)}`);
    } finally {
      setSoldPending(false);
    }
  }

  if (loadError) {
    return (
      <main className="container tight">
        <Link to="/inventory" className="ci-back">← Queue</Link>
        <div className="panel" style={{ marginTop: 18, borderColor: "var(--over)", color: "var(--over)" }}>
          Couldn't load this unit: {loadError.message || String(loadError)}
        </div>
      </main>
    );
  }
  if (!unit) {
    return (
      <main className="container tight">
        <Link to="/inventory" className="ci-back">← Queue</Link>
        <div className="panel" style={{ marginTop: 18, color: "var(--text-dim)" }}>Loading unit…</div>
      </main>
    );
  }

  const cc = chargeCycleStatus(unit);
  const ds = daysSinceCheck(unit);
  const elapsed = elapsedCharging(unit);
  const heading = [unit.year, unit.make, unit.model, unit.trim].filter(Boolean).join(" ") || unit.stockNumber;
  const recentHistory = (unit.history || []).slice().reverse().slice(0, 6);

  // Big lead number — days since last charge / on lot for never-checked.
  // Mirrors the lot walk card so the form feels like a continuation of
  // the same screen, not a separate context.
  const leadDays  = ds != null ? ds : (unit.daysOnLot ?? 0);
  const leadLabel = ds != null ? "since charge" : "on lot · new";

  return (
    <main className="container tight">
      <Link to="/inventory" className="ci-back">← Queue</Link>

      <div className="eyebrow" style={{ marginTop: 18 }}>Check-in</div>
      <h1 className="display" style={{ fontSize: 28, marginTop: 6 }}>
        {heading}
      </h1>
      <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--text-muted)" }}>
        <span className="mono">{unit.stockNumber || "—"}</span>
        {unit.department ? <> · {unit.department}</> : null}
        {" · "}{unit.daysOnLot ?? 0} days on lot
        {unit.lotPos ? <> · Lot {unit.lotPos}</> : null}
      </div>

      {/* Status block — same visual rhythm as the lot walk card. */}
      <div className="ci-last">
        <span className={"v " + (cc === "overdue" ? "over" : cc === "due" ? "warn" : cc === "charging" ? "hv" : "ok")}>
          {leadDays}
          <span style={{ fontSize: 14, fontWeight: 500, opacity: 0.65, marginLeft: 2 }}>d</span>
        </span>
        <span className="vs">
          {leadLabel}
          {cc === "charging" && elapsed && <> · charging {elapsed}</>}
          {cc !== "charging" && unit.lastAction && <> · {unit.lastAction}</>}
          {unit.lastCheckAt && <> · {relativeTime(unit.lastCheckAt)}</>}
        </span>
      </div>

      <form onSubmit={onSubmit} style={{ marginTop: 18 }}>
        <div className="field">
          <label>Action</label>
          <select value={action} onChange={(e) => setAction(e.target.value)} disabled={!writable || submitting}>
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div className="field">
          <label>Voltage <span style={{ color: "var(--text-dim)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
          <input
            className="big"
            type="text"
            inputMode="decimal"
            placeholder="12.62"
            value={voltage}
            onChange={(e) => setVoltage(e.target.value)}
            disabled={!writable || submitting}
          />
        </div>

        <div className="field">
          <label>Lot position</label>
          <input
            type="text"
            placeholder="Lot A · Row 3"
            value={lotPos}
            onChange={(e) => setLot(e.target.value)}
            disabled={!writable || submitting}
          />
        </div>

        <div className="field">
          <label>Sticky note</label>
          <textarea
            rows={2}
            placeholder="Customer pickup at 3pm · keys with finance · do NOT move"
            value={stickyNote}
            onChange={(e) => setSticky(e.target.value)}
            disabled={!writable || submitting}
          />
        </div>

        <div className="field">
          <label>Check-in notes (this entry only)</label>
          <textarea
            rows={2}
            placeholder="terminals corroded, recommended replacement"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!writable || submitting}
          />
        </div>

        {submitError && (
          <div style={{ color: "var(--over)", marginBottom: 14, fontSize: 13 }}>
            {submitError.message || String(submitError)}
          </div>
        )}

        <button type="submit" className="ci-submit" disabled={!writable || submitting}>
          {submitting ? "Saving…" : "Record check-in"}
        </button>
        <Link to="/inventory" className="ci-cancel">Cancel</Link>

        {!writable && (
          <p style={{ marginTop: 14, fontSize: 12.5, color: "var(--text-dim)" }}>
            Your role doesn't permit edits. Ask an admin for editor access.
          </p>
        )}
      </form>

      {/* Manager-only — flip the sold flag without waiting for the next
          DAR re-import. Sold units drop out of every queue immediately;
          a reactivate option appears if the unit is already marked sold. */}
      {canManage(profile) && unit && (
        <section
          style={{
            marginTop: 24,
            padding: "16px 18px",
            background: "var(--panel)",
            border: "1px solid var(--line)",
            borderRadius: 14,
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 10 }}>Manager actions</div>
          {unit.sold ? (
            <>
              <div style={{ fontSize: 13.5, color: "var(--text-muted)", marginBottom: 12 }}>
                This unit is marked <b style={{ color: "var(--amber)" }}>sold</b>. It's hidden from the
                lot walk and the manager queues. Reactivate to put it back in the cycle.
              </div>
              <button
                type="button"
                onClick={toggleSold}
                disabled={soldPending}
                className="btn"
                style={{ padding: "8px 14px" }}
              >
                {soldPending ? "Working…" : "Reactivate unit"}
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13.5, color: "var(--text-muted)", marginBottom: 12 }}>
                Mark this unit sold to drop it from every queue without waiting for the next
                DAR re-import. Reversible.
              </div>
              <button
                type="button"
                onClick={toggleSold}
                disabled={soldPending}
                className="btn"
                style={{
                  padding: "8px 14px",
                  background: "var(--warn-soft)",
                  borderColor: "rgba(176,94,19,0.32)",
                  color: "var(--amber)",
                }}
              >
                {soldPending ? "Working…" : "Mark sold"}
              </button>
            </>
          )}
        </section>
      )}

      {recentHistory.length > 0 && (
        <section className="ci-history">
          <h3>Recent history</h3>
          {recentHistory.map((h, i) => (
            <div key={i} className="ci-history-row">
              <span className="when">{relativeTime(h.at)}</span>
              <span>{h.action}</span>
              <span className="v">
                {h.voltage != null ? `${Number(h.voltage).toFixed(2)} V` : "—"}
              </span>
              <span className="who-avatar" title={h.by?.email || ""}>
                {h.by?.initials || (h.by?.email || "?").slice(0, 2)}
              </span>
            </div>
          ))}
        </section>
      )}
    </main>
  );
}
